import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { Container, Card, Button } from 'react-bootstrap';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AppLoading from 'src/components/common/AppLoading';
import { PAYROLL_DETAIL, PAYROLL_EDIT, PAYROLL_NEW } from 'src/utils/constants';
import { getListIdsServiceAdministrativeByUser } from 'src/redux/administrativeService/administrativeServiceReducer';
import { getGlobalDataSelectedPeriod } from 'src/redux/globalData/globalDataReducer';
import { getAccessToken } from 'src/redux/login/loginReducer';
import { payrollService } from 'src/services/payrollService';
import PayrollTableWithPagination from './PayrollTableWithPagination';
import { AdministrativeServiceData } from 'src/utils/administrativeServiceUtils';
import { setPayrollListFilter } from 'src/redux/payroll/payrollActions';
import { getPayrollListFilter } from 'src/redux/payroll/payrollReducer';

const PayrollListPage = () => {
	const dispatch = useDispatch();
	const shouldFetchOnFilterChange = useRef(false);
	const [isFetching, setIsFetching] = useState(false);
	const [payrollData, setPayrollData] = useState(null);
	const selectedPeriod = useSelector(state => getGlobalDataSelectedPeriod(state));
	const listIdsServiceAdministrative = useSelector(state => getListIdsServiceAdministrativeByUser(state));
	const [isDeleting, setIsDeleting] = useState(false);
	const accessToken = useSelector(state => getAccessToken(state));
	const administrativeServiceData = AdministrativeServiceData();
	const payrollListFilter = useSelector(state => getPayrollListFilter(state)?.data);
	const [serviceId, setServiceId] = useState(payrollListFilter?.administrativeServiceId || listIdsServiceAdministrative);
	const defaultParams = {
		page: 0,
		size: 10,
		sort: 'id,desc',
		administrativeServiceId: listIdsServiceAdministrative,
		year: selectedPeriod?.year
	};

	//persistent filter
	const [persistentFilter, setPersistentFilter] = useState({ ...payrollListFilter, administrativeServiceId: serviceId });

	const fetchPayrollList = async (pageNumber = 0) => {
		try {
			setIsFetching(true);
			const params = {
				page: pageNumber,
				size: 10,
				sort: 'id,desc',
				administrativeServiceId: serviceId,
				year: selectedPeriod?.year,
				...(persistentFilter.id && { id: persistentFilter.id }),
				...(persistentFilter.wageLiquidationType && { wageLiquidationType: persistentFilter.wageLiquidationType }),
				...(persistentFilter.name && { name: persistentFilter.name }),
				...(persistentFilter.description && { description: persistentFilter.description }),
				...(persistentFilter.netAmount && { netAmount: persistentFilter.netAmount }),
				...(persistentFilter.generalAmount && { generalAmount: persistentFilter.generalAmount }),
			};
			const data = await payrollService.getPayrollList(accessToken, params);
			setPayrollData(data);
			dispatch(setPayrollListFilter(persistentFilter))
		} catch (error) {
			console.error("Error:", error);
		} finally {
			setIsFetching(false);
		}
	};

	const handleAdministrativeService = value => {
		const serviceValue = value != '' ? value : defaultParams?.administrativeServiceId
		setServiceId(serviceValue);
		setPersistentFilter({
			...persistentFilter,
			administrativeServiceId: serviceValue,
		});
	};

	const onPageChange = (pNumber) => {
		fetchPayrollList(pNumber - 1);
	};

	const handleFilterChange = (filterChanges) => {
		setPersistentFilter(prev => ({
			...prev,
			...filterChanges
		}));
	};

	const clearFilters = () => {
		setPersistentFilter({});
		dispatch(setPayrollListFilter({}));
		setServiceId(listIdsServiceAdministrative);
		fetchPayrollList(0);
		shouldFetchOnFilterChange.current = true;
	};

	const handleApplyFilters = () => {
		shouldFetchOnFilterChange.current = true;
	};

	const onClickNewProvisionOrder = () => {
		dispatch(push(PAYROLL_NEW));
	};

	const onClickDetail = payroll => {
		dispatch(push(`${PAYROLL_DETAIL}/${payroll.id}`))
	};

	const onClickEdit = payroll => {
		dispatch(push(`${PAYROLL_EDIT}/${payroll.id}`))
	};

	const onClickDelete = payroll => {
		swal({
			title: 'Confirmación',
			text: `¿Desea eliminar la Liquidación: ${payroll.description}?`,
			icon: 'warning',
			buttons: ["Cancelar", "Aceptar"],
			dangerMode: true,
		}).then(async (willDelete) => {
			if (willDelete) {
				setIsDeleting(true);
				try {
					const result = await payrollService.deletePayroll(accessToken, payroll.id);
					if (result.success) {
						fetchPayrollList(payrollData?.metadata?.page || 0);
					}
				} catch (error) {
					console.error("Error:", error);
				} finally {
					setIsDeleting(false);
				}
			}
		});
	};

	useEffect(() => {
		if (accessToken && selectedPeriod?.year) {
			fetchPayrollList(0);
		}
	}, [serviceId]);

	useEffect(() => {
		fetchPayrollList(0);
	}, [accessToken, listIdsServiceAdministrative, selectedPeriod?.year]);

	useEffect(() => {
		if (shouldFetchOnFilterChange.current) {
			fetchPayrollList(0);
			shouldFetchOnFilterChange.current = false;
		}
	}, [persistentFilter]);

	return <>
		<Container fluid className='px-1 px-lg-3'>
			<Card className='mb-5'>
				<Card.Header className='h6'>
					Liquidación de Haberes
				</Card.Header>
				<Card.Body>
					<div className='d-flex'>
						<Button size='md' className='mb-3 btn-success' onClick={onClickNewProvisionOrder}>
							<FontAwesomeIcon icon={faPlus} className='mr-1' />
							Nueva Liquidación de Haberes
						</Button>
						{administrativeServiceData?.length > 1 &&
							<select
								value={serviceId}
								className='form-control col-3 mx-3'
								onChange={(e) => handleAdministrativeService(e.target.value)}
							>
								<option value={''}>Todos los servicios</option>
								{Array.isArray(administrativeServiceData) &&
									administrativeServiceData.map(item => (
										<option className='py-2' value={item.id} key={item.id}>
											{item.code} - {item.shortName}
										</option>
									))
								}
							</select>
						}
					</div>
					<PayrollTableWithPagination
						payrollData={payrollData}
						isFetching={isFetching}
						mode="LIST"
						onPageChange={onPageChange}
						onDetail={onClickDetail}
						onEdit={onClickEdit}
						onDelete={onClickDelete}
						filters={persistentFilter}
						onFilterChange={handleFilterChange}
						onClearFilters={clearFilters}
						onApplyFilters={handleApplyFilters}
					/>
				</Card.Body>
			</Card>
			<AppLoading isLoading={isFetching || isDeleting} />
		</Container>
	</>
};

export default PayrollListPage;
